import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Textarea,
} from '@/components/ui'
import { customerApi } from '../lib/api'

interface TanssPerson {
  id: number
  name?: string
  firstName?: string
  lastName?: string
  salutation?: string
  title?: string
  emailAddress?: string
  email?: string
  mail?: string
  privateEmail?: string
  telephoneNumber?: string
  telephoneNumberTwo?: string
  phone?: string
  mobilePhone?: string
  mobileNumberTwo?: string
  mobile?: string
  privatePhoneNumber?: string
  room?: string
  role?: string
  birthday?: string
  active?: boolean
  categories?: number[]
}

interface Category {
  id: number
  name: string
}

interface MutationResult {
  id: number
  warnings?: string[]
}

interface DeletionRequestResult {
  ticketId: number | null
  employeeName: string
}

const columnHelper = createColumnHelper<TanssPerson>()

const SALUTATION_NONE = '__none__'
const SALUTATION_OPTIONS = [
  { value: SALUTATION_NONE, label: '— keine —' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
  { value: 'Divers', label: 'Divers' },
]

function personName(p: TanssPerson): string {
  if (p.name) return p.name
  const fn = [p.lastName, p.firstName].filter(Boolean).join(', ')
  return fn || `#${p.id}`
}

function splitName(person: TanssPerson): { firstName: string; lastName: string } {
  if (person.firstName || person.lastName) {
    return { firstName: person.firstName ?? '', lastName: person.lastName ?? '' }
  }
  if (person.name) {
    // TANSS-Listen-Format: "Nachname, Vorname"
    const commaIdx = person.name.indexOf(',')
    if (commaIdx > -1) {
      return {
        lastName: person.name.slice(0, commaIdx).trim(),
        firstName: person.name.slice(commaIdx + 1).trim(),
      }
    }
    return { firstName: '', lastName: person.name.trim() }
  }
  return { firstName: '', lastName: '' }
}

type Mode = { kind: 'create' } | { kind: 'edit'; person: TanssPerson }

interface ExtractedError {
  title: string
  message: string
  status?: number
  code?: string
  raw?: string
}

function extractError(err: unknown, fallback: string): ExtractedError {
  const e = err as {
    message?: string
    backendCode?: string
    response?: { status?: number; data?: unknown }
  } | null
  const status = e?.response?.status
  const data = e?.response?.data
  let message = fallback
  let code: string | undefined = e?.backendCode
  let raw: string | undefined

  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>
    if (typeof body['error'] === 'string') message = body['error'] as string
    else if (typeof body['message'] === 'string') message = body['message'] as string
    if (typeof body['code'] === 'string') code = body['code'] as string
    try {
      raw = JSON.stringify(body).slice(0, 800)
    } catch {
      /* ignore */
    }
  } else if (typeof data === 'string' && data.length > 0) {
    raw = data.slice(0, 800)
    if (data.length < 200) message = data
  } else if (e?.message) {
    message = e.message
  }

  return {
    title: fallback,
    message,
    status,
    code,
    raw,
  }
}

function errorDetails(e: ExtractedError): string {
  const lines: string[] = []
  lines.push(e.message)
  if (e.status) lines.push(`HTTP-Status: ${e.status}`)
  if (e.code) lines.push(`Code: ${e.code}`)
  if (e.raw) lines.push(`Response: ${e.raw}`)
  return lines.join('\n')
}

export function CustomerEmployeesPage() {
  const qc = useQueryClient()
  const employees = useQuery<{ data: TanssPerson[] }>({
    queryKey: ['customer-employees'],
    queryFn: () => customerApi.get<{ data: TanssPerson[] }>('/employees').then((r) => r.data),
  })
  const categories = useQuery<{ data: Category[] }>({
    queryKey: ['customer-employee-categories'],
    queryFn: () =>
      customerApi.get<{ data: Category[] }>('/employees/categories').then((r) => r.data),
  })

  const [mode, setMode] = useState<Mode | null>(null)
  const [salutation, setSalutation] = useState('')
  const [title, setTitle] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState('')
  const [room, setRoom] = useState('')
  const [telephoneNumber, setPhone] = useState('')
  const [telephoneNumberTwo, setPhone2] = useState('')
  const [mobilePhone, setMobile] = useState('')
  const [mobileNumberTwo, setMobilePrivate] = useState('')
  const [privatePhoneNumber, setPrivatePhone] = useState('')
  const [emailAddress, setEmail] = useState('')
  const [privateEmail, setPrivateEmail] = useState('')
  const [birthday, setBirthday] = useState('')
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])
  const [submitError, setSubmitError] = useState<ExtractedError | null>(null)
  const [lastWarnings, setLastWarnings] = useState<string[] | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<TanssPerson | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteError, setDeleteError] = useState<ExtractedError | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState<DeletionRequestResult | null>(null)

  const categoryById = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of categories.data?.data ?? []) map.set(c.id, c.name)
    return map
  }, [categories.data])

  // Formular fuellen / zuruecksetzen, wenn sich der Modus aendert
  useEffect(() => {
    if (!mode) return
    setSubmitError(null)
    if (mode.kind === 'create') {
      setSalutation(SALUTATION_NONE)
      setTitle('')
      setFirstName('')
      setLastName('')
      setRole('')
      setRoom('')
      setPhone('')
      setPhone2('')
      setMobile('')
      setMobilePrivate('')
      setPrivatePhone('')
      setEmail('')
      setPrivateEmail('')
      setBirthday('')
      setSelectedCategoryIds([])
      return
    }
    const p = mode.person
    const { firstName: fn, lastName: ln } = splitName(p)
    setSalutation(p.salutation && p.salutation.length > 0 ? p.salutation : SALUTATION_NONE)
    setTitle(p.title ?? '')
    setFirstName(fn)
    setLastName(ln)
    setRole(p.role ?? '')
    setRoom(p.room ?? '')
    setPhone(p.telephoneNumber ?? p.phone ?? '')
    setPhone2(p.telephoneNumberTwo ?? '')
    setMobile(p.mobilePhone ?? p.mobile ?? '')
    setMobilePrivate(p.mobileNumberTwo ?? '')
    setPrivatePhone(p.privatePhoneNumber ?? '')
    setEmail(p.emailAddress ?? p.email ?? p.mail ?? '')
    setPrivateEmail(p.privateEmail ?? '')
    setBirthday(p.birthday ?? '')
    setSelectedCategoryIds(p.categories ?? [])
  }, [mode])

  function buildPayload() {
    return {
      salutation: salutation === SALUTATION_NONE ? '' : salutation,
      title,
      firstName,
      lastName,
      role,
      room,
      telephoneNumber,
      telephoneNumberTwo,
      mobilePhone,
      mobileNumberTwo,
      privatePhoneNumber,
      emailAddress,
      privateEmail,
      birthday,
      categoryIds: selectedCategoryIds,
    }
  }

  const createMutation = useMutation<MutationResult, unknown, void>({
    mutationFn: async () => {
      const res = await customerApi.post<MutationResult>('/employees', buildPayload())
      return res.data
    },
    onSuccess: (data) => {
      setLastWarnings(data.warnings ?? null)
      setSubmitError(null)
      setMode(null)
      void qc.invalidateQueries({ queryKey: ['customer-employees'] })
    },
    onError: (err) => {
      setSubmitError(extractError(err, 'Anlegen fehlgeschlagen'))
    },
  })

  const updateMutation = useMutation<MutationResult, unknown, number>({
    mutationFn: async (id) => {
      const res = await customerApi.put<MutationResult>(`/employees/${id}`, buildPayload())
      return res.data
    },
    onSuccess: (data) => {
      setLastWarnings(data.warnings ?? null)
      setSubmitError(null)
      setMode(null)
      void qc.invalidateQueries({ queryKey: ['customer-employees'] })
    },
    onError: (err) => {
      setSubmitError(extractError(err, 'Speichern fehlgeschlagen'))
    },
  })

  const deleteRequestMutation = useMutation<DeletionRequestResult, unknown, { id: number; reason: string }>({
    mutationFn: async ({ id, reason }) => {
      const res = await customerApi.post<DeletionRequestResult>(
        `/employees/${id}/deletion-request`,
        { reason },
      )
      return res.data
    },
    onSuccess: (data) => {
      setDeleteError(null)
      setDeleteSuccess(data)
      setDeleteTarget(null)
      setDeleteReason('')
    },
    onError: (err) => {
      setDeleteError(extractError(err, 'Löschantrag konnte nicht erstellt werden'))
    },
  })

  const isEdit = mode?.kind === 'edit'
  const mutation = isEdit ? updateMutation : createMutation
  const isPending = mutation.isPending

  const columns = useMemo<ColumnDef<TanssPerson, unknown>[]>(
    () => [
      columnHelper.accessor((row) => personName(row), {
        id: 'name',
        header: 'Name',
        cell: (ctx) => <span className="font-medium">{ctx.getValue()}</span>,
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.accessor((row) => row.role ?? '', {
        id: 'role',
        header: 'Funktion',
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.accessor((row) => row.emailAddress ?? row.email ?? row.mail ?? '', {
        id: 'email',
        header: 'E-Mail',
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.accessor((row) => row.telephoneNumber ?? row.phone ?? '', {
        id: 'phone',
        header: 'Telefon',
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.accessor((row) => row.mobilePhone ?? row.mobile ?? '', {
        id: 'mobile',
        header: 'Mobil',
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.accessor((row) => row.categories ?? [], {
        id: 'categories',
        header: 'Kategorien',
        cell: (ctx) => {
          const ids = ctx.getValue()
          if (!ids || ids.length === 0) return <span className="text-gray-400">–</span>
          return ids.map((id) => categoryById.get(id) ?? `#${id}`).join(', ')
        },
      }) as ColumnDef<TanssPerson, unknown>,
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (ctx) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                setMode({ kind: 'edit', person: ctx.row.original })
              }}
            >
              Bearbeiten
            </Button>
            <Button
              variant="danger"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                setDeleteError(null)
                setDeleteReason('')
                setDeleteTarget(ctx.row.original)
              }}
            >
              Löschen
            </Button>
          </div>
        ),
      }) as ColumnDef<TanssPerson, unknown>,
    ],
    [categoryById],
  )

  function toggleCategory(id: number) {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  function submit() {
    if (mode?.kind === 'edit') {
      updateMutation.mutate(mode.person.id)
    } else {
      createMutation.mutate()
    }
  }

  if (employees.isLoading) return <LoadingState />
  if (employees.error instanceof Error) {
    return (
      <div>
        <PageHeader title="Mitarbeiter" />
        <ErrorState
          title="Mitarbeiter konnten nicht geladen werden"
          details={employees.error.message}
        />
      </div>
    )
  }

  const people = employees.data?.data ?? []
  const cats = categories.data?.data ?? []

  return (
    <div>
      <PageHeader
        title="Mitarbeiter"
        description="Ansprechpartner in TANSS anzeigen, anlegen, bearbeiten und mit Kategorien versehen."
        actions={
          <Button variant="primary" size="sm" onClick={() => setMode({ kind: 'create' })}>
            Neuer Mitarbeiter
          </Button>
        }
      />

      {lastWarnings && lastWarnings.length > 0 && (
        <div className="mb-4 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-small text-yellow-900">
          <div className="font-medium mb-1">Gespeichert mit Warnungen:</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {lastWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {deleteSuccess && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-small text-green-900">
          <div className="font-medium mb-1">Löschantrag erstellt</div>
          <div>
            Für {deleteSuccess.employeeName} wurde ein Ticket
            {deleteSuccess.ticketId ? ` (#${deleteSuccess.ticketId})` : ''} an implec angelegt.
            Der Mitarbeiter wird nach Prüfung entfernt.
          </div>
          <div className="mt-2">
            <Button variant="ghost" size="xs" onClick={() => setDeleteSuccess(null)}>
              Schließen
            </Button>
          </div>
        </div>
      )}

      <Card>
        <Card.Body>
          {people.length === 0 ? (
            <EmptyState
              title="Noch keine Mitarbeiter in TANSS"
              description="Lege den ersten Mitarbeiter für deine Firma an."
            />
          ) : (
            <DataTable
              data={people}
              columns={columns}
              getRowId={(row) => String(row.id)}
              onRowClick={(row) => setMode({ kind: 'edit', person: row })}
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        open={mode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMode(null)
            setSubmitError(null)
          }
        }}
        size="lg"
        title={isEdit ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
        description={
          isEdit
            ? 'Änderungen werden direkt in TANSS gespeichert.'
            : 'Wird direkt in TANSS angelegt und mit deiner Firma verknüpft.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setMode(null)} disabled={isPending}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={isPending || !firstName.trim() || !lastName.trim()}
            >
              {isPending
                ? isEdit
                  ? 'Speichere …'
                  : 'Lege an …'
                : isEdit
                  ? 'Speichern'
                  : 'Anlegen'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Anrede"
            value={salutation}
            onValueChange={(v) => setSalutation(v)}
            options={SALUTATION_OPTIONS}
            placeholder="— keine —"
          />
          <Input label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input
            label="Vorname *"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Nachname *"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <Input label="Funktion" value={role} onChange={(e) => setRole(e.target.value)} />
          <Input label="Raum" value={room} onChange={(e) => setRoom(e.target.value)} />
          <Input
            label="Telefon"
            value={telephoneNumber}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Telefon 2"
            value={telephoneNumberTwo}
            onChange={(e) => setPhone2(e.target.value)}
          />
          <Input label="Mobil-Nr" value={mobilePhone} onChange={(e) => setMobile(e.target.value)} />
          <Input
            label="Private Mobil-Nr"
            value={mobileNumberTwo}
            onChange={(e) => setMobilePrivate(e.target.value)}
          />
          <Input
            label="Privat-Nr"
            value={privatePhoneNumber}
            onChange={(e) => setPrivatePhone(e.target.value)}
          />
          <Input
            label="E-Mail"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Private E-Mail"
            type="email"
            value={privateEmail}
            onChange={(e) => setPrivateEmail(e.target.value)}
          />
          <Input
            label="Geburtstag"
            type="date"
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
          />
        </div>

        <div className="mt-4">
          <div className="text-small font-medium text-gray-700 mb-2">Kategorien</div>
          {categories.isLoading ? (
            <div className="text-caption text-gray-500">Lade Kategorien …</div>
          ) : cats.length === 0 ? (
            <div className="text-caption text-gray-500">Keine Kategorien verfügbar.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => {
                const active = selectedCategoryIds.includes(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={
                      active
                        ? 'px-3 py-1 rounded-full text-small border bg-brand-100 border-brand-300 text-brand-900'
                        : 'px-3 py-1 rounded-full text-small border bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  >
                    {c.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {submitError && (
          <div className="mt-4">
            <ErrorState title={submitError.title} details={errorDetails(submitError)} />
          </div>
        )}
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteError(null)
            setDeleteReason('')
          }
        }}
        size="md"
        title="Mitarbeiter löschen"
        description="Ein Mitarbeiter wird nicht direkt gelöscht. Dein Löschantrag wird als Ticket an implec gesendet und dort bearbeitet."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteRequestMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!deleteTarget) return
                deleteRequestMutation.mutate({
                  id: deleteTarget.id,
                  reason: deleteReason.trim(),
                })
              }}
              disabled={deleteRequestMutation.isPending || deleteReason.trim().length < 3}
            >
              {deleteRequestMutation.isPending ? 'Sende …' : 'Löschantrag senden'}
            </Button>
          </>
        }
      >
        {deleteTarget && (
          <div className="space-y-3">
            <div className="text-small text-gray-700">
              <span className="font-medium">{personName(deleteTarget)}</span>
              {deleteTarget.role ? ` – ${deleteTarget.role}` : ''}
            </div>
            <Textarea
              label="Grund für die Löschung *"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              hint="Mindestens 3 Zeichen. Der Text wird als Ticket-Beschreibung an implec übermittelt."
              rows={4}
              placeholder="z.B. Mitarbeiter hat das Unternehmen zum 30.04. verlassen."
            />
            {deleteError && (
              <ErrorState title={deleteError.title} details={errorDetails(deleteError)} />
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
